import React, { memo, useMemo } from 'react';
import { View, ScrollView } from 'react-native';
import { createUseStyle } from '@redlink/assets/styles';
import { Text, IconPassed, Space } from '@redlink/components/atoms';
import { Badge } from '@redlink/components/molecules';
import { useLocale } from '@redlink/assets/locale';
import { useMoneyMask } from '@redlink/core';

export const PlanItem = memo(({ item: { key, price, finalPrice } = {} }) => {
  const { styles, colors } = useStyle();
  const { dictionary } = useLocale();
  const { getValue } = useMoneyMask();

  const monthlyPrice = useMemo(() => {
    return finalPrice / 12 - ((finalPrice / 12) % 1000);
  }, [finalPrice]);

  return (
    <View style={styles.container}>
      <View style={styles.headerContainer}>
        <Text style={styles.titleText}>{dictionary.bePro}</Text>
        <Space size={8} />
        <View style={styles.pricesContainer}>
          <Text style={styles.priceText}>{`${getValue(price)} ${
            dictionary.tomanPostfix
          }`}</Text>
          <Space size={8} />
          <Text style={styles.finalPriceText}>
            {`${getValue(finalPrice)} ${dictionary.tomanPostfixSummary}`}
          </Text>
          <Badge
            content={dictionary.annually}
            size={14}
            backgroundColor={colors.white2}
            color={colors.white}
          />
        </View>
        <Text style={styles.monthlyPriceText}>{`${
          dictionary.monthlyPrefix
        } ${getValue(monthlyPrice)} ${dictionary.tomanPostfix}`}</Text>
      </View>
      <ScrollView
        contentContainerStyle={styles.bodyContentContainer}
        showsVerticalScrollIndicator={false}
      >
        <Text style={styles.optionsTitleText}>
          {dictionary.planPageDescription}
        </Text>
        <Space />
        <OptionItem
          prefix={dictionary.planItem1Prefix}
          content={dictionary.planItem1Content}
        />
        <OptionItem
          prefix={dictionary.planItem2Prefix}
          content={dictionary.planItem2Content}
          postfix={dictionary.planItem2Postfix}
        />
        <OptionItem
          prefix={dictionary.planItem3Prefix}
          content={dictionary.planItem3Content}
          postfix={dictionary.planItem3Postfix}
        />
        <OptionItem
          prefix={dictionary.planItem4Prefix}
          content={dictionary.planItem4Content}
          postfix={dictionary.planItem4Postfix}
        />
        <OptionItem
          prefix={dictionary.planItem5Prefix}
          content={dictionary.planItem5Content}
          postfix={dictionary.planItem5Postfix}
        />
        <OptionItem
          prefix={dictionary.planItem6Prefix}
          content={dictionary.planItem6Content}
        />
        <OptionItem
          prefix={dictionary.planItem7Prefix}
          content={dictionary.planItem7Content}
          postfix={dictionary.planItem7Postfix}
        />
        <OptionItem
          prefix={dictionary.planItem8Prefix}
          content={dictionary.planItem8Content}
          postfix={dictionary.planItem8Postfix}
        />
        <OptionItem
          prefix={dictionary.planItem9Prefix}
          content={dictionary.planItem9Content}
        />
        <OptionItem
          prefix={dictionary.planItem10Prefix}
          content={dictionary.planItem10Content}
          soon
        />
      </ScrollView>
    </View>
  );
});

const OptionItem = memo(({ soon = false, prefix, content, postfix }) => {
  const { styles, colors } = useStyle();
  const { dictionary } = useLocale();
  return (
    <View style={styles.optionItemContainer}>
      <IconPassed color={colors.green1} size={20} />
      <Space size={8} />
      <Text style={styles.optionItemTitle}>
        {prefix && `${prefix} `}
        {content && <Text style={styles.optionItemBoldTitle}>{content}</Text>}
        {postfix && ` ${postfix}`}
      </Text>
      {soon && (
        <>
          <Space size={8} />
          <Badge content={dictionary.commingSoon} />
        </>
      )}
    </View>
  );
});

const useStyle = createUseStyle(({ colors, size, shadow }) => ({
  container: {
    flex: 1,
  },
  headerContainer: {
    borderRadius: 5,
    padding: 16,
    backgroundColor: colors.green1,
  },
  titleText: {
    fontSize: 20,
    color: colors.white,
  },
  subtitleText: {
    fontSize: 13,
    color: colors.white,
  },
  pricesContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  priceText: {
    fontSize: 13,
    color: colors.white,
    textDecorationLine: 'line-through',
    fontWeight: '600',
  },
  monthlyPriceText: {
    fontSize: 14,
    color: colors.white,
  },
  finalPriceText: {
    flex: 1,
    fontSize: 28,
    color: colors.white,
    fontWeight: 'bold',
  },
  optionItemContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
  },
  optionItemTextContainer: {
    flex: 1,
  },
  optionItemTitle: {
    color: colors.black4,
  },
  optionItemDescription: {
    fontSize: 14,
    color: colors.gray21,
  },
  optionItemBoldTitle: {
    fontWeight: 'bold',
  },
  optionAvailableItemTitle: {
    color: colors.green1,
  },
  bodyContentContainer: {
    paddingVertical: 16,
  },
  optionsTitleText: {
    paddingHorizontal: 24,
    color: colors.gray21,
  },
}));
